/**
 * Format a number as currency (USD)
 */
export function formatCurrency(value: number, decimals = 2): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format a number with commas
 */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

/**
 * Format a percentage
 */
export function formatPercentage(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format large numbers with K, M, B suffixes
 */
export function formatCompact(value: number): string {
  if (value >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(1)}B`;
  }
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }
  return value.toFixed(0);
}

/**
 * Format very small numbers appropriately
 * - For numbers >= 0.0001: show as currency
 * - For numbers < 0.0001: show full scientific notation
 */
export function formatSmallNumber(value: number): string {
  if (value === 0) {
    return '$0.00';
  }

  const absValue = Math.abs(value);

  // For very small numbers, show scientific notation
  if (absValue > 0 && absValue < 0.0001) {
    return `$${value.toExponential(2)}`;
  }

  // For small numbers, show more decimal places
  if (absValue < 0.01) {
    return `$${value.toFixed(6)}`;
  }

  if (absValue < 1) {
    return `$${value.toFixed(4)}`;
  }

  // Normal currency formatting
  return formatCurrency(value);
}

/**
 * Get color based on cost threshold
 */
export function getCostColor(cost: number): 'success' | 'warning' | 'error' {
  if (cost > 2000) return 'error';
  if (cost > 500) return 'warning';
  return 'success';
}

/**
 * Get color hex value based on cost threshold
 */
export function getCostColorHex(cost: number): string {
  if (cost > 2000) return '#f44336';
  if (cost > 500) return '#ff9800';
  return '#4caf50';
}

/**
 * Format date string
 */
export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Get display name for model ID
 * Note: Prefer using model_pricing[modelId].display_name from projectConfig when available
 */
export function getModelDisplayName(modelId: string): string {
  // Just return the model ID - display names should come from projectConfig.model_pricing
  return modelId;
}

/**
 * Chart colors palette
 */
export const CHART_COLORS = [
  '#1a237e', // Primary blue
  '#00897b', // Teal
  '#f44336', // Red
  '#ff9800', // Orange
  '#4caf50', // Green
  '#9c27b0', // Purple
  '#00bcd4', // Cyan
  '#795548', // Brown
  '#607d8b', // Blue grey
];
